/**
 * Full-page view for essay version history.
 *
 * Called by:
 * - App.tsx as route for /reviewer/essays/:slug/history
 */
import { useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Typography, Button, Space, Spin } from 'antd';
import { ArrowLeftOutlined, HistoryOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getEssay } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import VersionHistory from '../components/VersionHistory';

const { Title, Text } = Typography;

export default function EssayHistoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: essay, isLoading } = useQuery({
    queryKey: ['essay', slug],
    queryFn: () => getEssay(slug!),
    enabled: !!slug,
  });

  const handleRevert = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['essay', slug] });
    queryClient.invalidateQueries({ queryKey: ['versions', slug] });
  }, [queryClient, slug]);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link to={`/reviewer/essays/${slug}`}>
          <Button type="text" icon={<ArrowLeftOutlined />} style={{ paddingLeft: 0, marginBottom: 8 }}>
            Back to Essay
          </Button>
        </Link>
        <Space>
          <HistoryOutlined style={{ fontSize: 20, color: '#d4af37' }} />
          <Title level={3} style={{ margin: 0 }}>Version History</Title>
        </Space>
        {essay && (
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            {essay.title}
          </Text>
        )}
      </div>

      {/* Version history panel */}
      {user && (
        <VersionHistory
          slug={slug!}
          user={user}
          onRevert={handleRevert}
        />
      )}
    </div>
  );
}
